import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { BrandMark } from "../components/Brand";
import { api, formatApiError, formatDate } from "../lib/api";
import { fetchAdminMe } from "../lib/auth";
import { ArrowLeft, Send, Copy, CheckCircle2, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminInvitations() {
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", firm: "" });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        await fetchAdminMe();
        await load();
      } catch {
        navigate("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/invitations");
      setInvites(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/invitations", form);
      toast.success(`Invitation sent to ${form.email}`);
      setForm({ email: "", name: "", firm: "" });
      navigator.clipboard?.writeText(data.invite_url).catch(() => {});
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/attorney-login?invite=${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Invite link copied");
  };

  return (
    <div className="bg-[#F9FAFB] min-h-full" data-testid="admin-invitations-page">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-8 py-10 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2">
          <div className="uppercase-label">Onboarding</div>
          <h1 className="font-display text-3xl text-[#0A2540] font-semibold mt-1">Invite an attorney</h1>
          <p className="text-[#6B7280] mt-2 text-sm leading-relaxed">
            Send a one-time secure activation link. The attorney signs in with Google to access their portal — no password needed.
          </p>

          <form onSubmit={submit} className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl p-6 space-y-4" data-testid="invite-form">
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="attorney@firm.com" className="mt-1.5" data-testid="invite-email" />
            </div>
            <div>
              <Label htmlFor="name">Name (optional)</Label>
              <Input id="name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Jane Smith" className="mt-1.5" data-testid="invite-name" />
            </div>
            <div>
              <Label htmlFor="firm">Firm (optional)</Label>
              <Input id="firm" value={form.firm} onChange={(e) => setForm((f) => ({ ...f, firm: e.target.value }))} placeholder="Smith & Partners" className="mt-1.5" data-testid="invite-firm" />
            </div>
            <Button type="submit" className="btn-primary-navy w-full h-11" disabled={submitting} data-testid="invite-submit">
              {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>) : (<><Send className="w-4 h-4 mr-2" />Send invitation</>)}
            </Button>
            <p className="text-xs text-[#6B7280]">Invitations expire in 14 days. The link will also be copied to your clipboard.</p>
          </form>
        </div>

        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl text-[#0A2540] font-semibold">Recent invitations</h2>
            <span className="text-xs text-[#6B7280]">{invites.length} total</span>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden">
            {loading && <div className="p-12 text-center text-[#6B7280]">Loading…</div>}
            {!loading && invites.length === 0 && (
              <div className="p-12 text-center text-[#6B7280]" data-testid="invites-empty">No invitations yet. Send your first above →</div>
            )}
            {!loading && invites.map((inv) => (
              <div key={inv.token} className="px-6 py-4 border-b border-[#F3F4F6] last:border-b-0 flex flex-wrap items-center justify-between gap-3" data-testid={`invite-row-${inv.token.slice(0,8)}`}>
                <div className="min-w-0">
                  <div className="font-medium text-[#0A2540]">{inv.email}</div>
                  <div className="text-xs text-[#6B7280]">{[inv.name, inv.firm].filter(Boolean).join(" · ") || "—"} · sent {formatDate(inv.created_at)}</div>
                </div>
                <div className="flex items-center gap-3">
                  {inv.accepted_at ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-[#047857]"><CheckCircle2 className="w-3.5 h-3.5" /> Activated</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs text-[#92400E]"><Clock className="w-3.5 h-3.5" /> Pending</span>
                  )}
                  <Button size="sm" variant="outline" onClick={() => copyLink(inv.token)} data-testid={`copy-invite-${inv.token.slice(0,8)}`}>
                    <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy link
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
